import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import { Textarea } from "../components/ui/textarea";
import { Loader2, Zap, ArrowLeft, Check, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { trackCallBooked } from "../lib/analytics";
import { usePageSEO } from "../lib/seo";

export default function BookingPage() {
  const { auditId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  usePageSEO({ title: "Book a Strategy Call", description: "Schedule a 30-minute AI growth strategy session with the Stansbury AI team. Discover where AI can drive revenue for your business.", path: "/booking" });
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [booked, setBooked] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [blockedDates, setBlockedDates] = useState(new Set());
  const [error, setError] = useState("");

  // Pre-fill form with user data or URL params (from chatbot)
  useEffect(() => {
    const urlName = searchParams.get("name");
    const urlEmail = searchParams.get("email");
    const urlWebsite = searchParams.get("website");
    setForm(f => ({
      ...f,
      name: urlName || (user?.name) || f.name,
      email: urlEmail || (user?.email) || f.email,
      website: urlWebsite || f.website,
    }));
  }, [user, searchParams]);

  // Scroll to top on mount
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Load Canadian holidays
  useEffect(() => {
    const loadHolidays = async () => {
      try {
        const { data } = await api.get("/booking/blocked-dates");
        setBlockedDates(new Set(data.holidays || []));
      } catch (e) { console.error(e); }
    };
    loadHolidays();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedDate) {
      const fetchSlots = async () => {
        setSlotsLoading(true);
        try {
          const dateStr = selectedDate.toISOString().split("T")[0];
          const { data } = await api.get(`/booking/available-slots?date=${dateStr}`);
          setAvailableSlots(data.slots || []);
          setSelectedSlot("");
        } catch (err) {
          console.error(err);
        } finally {
          setSlotsLoading(false);
        }
      };
      fetchSlots();
    }
  }, [selectedDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedSlot || !form.name || !form.email) return;
    setLoading(true);
    setError("");
    try {
      const dateStr = selectedDate.toISOString().split("T")[0];
      const payload = { audit_id: auditId || "", date: dateStr, time_slot: selectedSlot, ...form };
      // Use public endpoint if not logged in, authenticated endpoint if logged in
      const endpoint = user ? "/booking/create" : "/booking/public";
      const { data } = await api.post(endpoint, payload);
      setBooked(true);
      setBookingDetails(data);
      trackCallBooked(dateStr, user ? "authenticated" : "public");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to book. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (booked) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6" data-testid="booking-success">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white border border-slate-200 p-10 text-center max-w-md w-full">
          <div className="w-16 h-16 bg-[#E5EDFF] flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-[#0033CC]" />
          </div>
          <h2 className="text-2xl font-['Outfit'] font-medium text-[#0F172A] mb-3">Strategy Call Booked</h2>
          <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-6">
            Your call is confirmed for {bookingDetails?.date} at {bookingDetails?.time_slot}.
          </p>
          <div className="bg-slate-50 border border-slate-100 p-4 mb-6 text-left">
            <div className="text-xs font-['JetBrains_Mono'] text-slate-400 uppercase mb-2">Details</div>
            <p className="text-sm text-slate-700 font-['IBM_Plex_Sans']">Date: {bookingDetails?.date}</p>
            <p className="text-sm text-slate-700 font-['IBM_Plex_Sans']">Time: {bookingDetails?.time_slot}</p>
            <p className="text-sm text-slate-700 font-['IBM_Plex_Sans']">Status: {bookingDetails?.status}</p>
          </div>
          <Button onClick={() => user ? navigate("/dashboard") : navigate("/")} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none px-8 h-11 font-['Outfit']" data-testid="booking-go-dashboard">
            {user ? "Return to Dashboard" : "Back to Home"}
          </Button>
        </motion.div>
      </div>
    );
  }

  // Must book at least 1 day ahead (no same-day)
  const today = new Date();
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() + 1);

  const isDateDisabled = (date) => {
    if (date <= today) return true; // No same-day or past
    if (date.getDay() === 0 || date.getDay() === 6) return true; // Weekends
    const dateStr = date.toISOString().split("T")[0];
    if (blockedDates.has(dateStr)) return true; // Canadian holidays
    return false;
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="booking-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 md:px-12 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/stansbury-logo.png" alt="Stansbury AI" className="h-12" />
        </div>
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 font-['Outfit']" data-testid="booking-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        <div className="mb-8">
          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-3">Book a Call</p>
          <h1 className="text-2xl sm:text-3xl font-['Outfit'] font-medium tracking-tight text-[#0F172A] mb-2">Schedule Your Strategy Session</h1>
          <p className="text-sm text-slate-500 font-['IBM_Plex_Sans']">Sessions are 30 minutes, available Monday–Friday between 10:00 AM and 3:00 PM EST.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Calendar */}
          <div className="bg-white border border-slate-200 p-6" data-testid="booking-calendar">
            <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Select a Date</h3>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              disabled={isDateDisabled}
              className="w-full"
            />

            {selectedDate && (
              <div className="mt-6">
                <h4 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#0033CC]" /> Available Times
                </h4>
                {slotsLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-[#0033CC]" />
                ) : availableSlots.length === 0 ? (
                  <p className="text-sm text-slate-400 font-['IBM_Plex_Sans']">No slots available for this date.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2" data-testid="time-slots">
                    {availableSlots.map((slot) => (
                      <button key={slot} onClick={() => setSelectedSlot(slot)}
                        className={`p-2 border text-sm font-['IBM_Plex_Sans'] transition-all ${
                          selectedSlot === slot ? "border-[#0033CC] bg-[#E5EDFF] text-[#0033CC]" : "border-slate-200 text-slate-600 hover:border-slate-300"
                        }`} data-testid={`time-slot-${slot}`}>
                        {slot}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Form */}
          <div className="bg-white border border-slate-200 p-6" data-testid="booking-form">
            <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Your Details</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">Full Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5 rounded-none border-slate-300" data-testid="booking-name-input" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5 rounded-none border-slate-300" data-testid="booking-email-input" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Company Website *</Label>
                <Input type="url" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} required placeholder="https://yourcompany.com" className="mt-1.5 rounded-none border-slate-300" data-testid="booking-website-input" />
                <p className="text-xs text-slate-400 mt-1 font-['IBM_Plex_Sans']">We review your site before the call to provide tailored insights</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Phone (Optional)</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-none border-slate-300" data-testid="booking-phone-input" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Notes (Optional)</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any specific topics you'd like to discuss?" className="mt-1.5 rounded-none border-slate-300 min-h-[80px]" data-testid="booking-notes-input" />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3" data-testid="booking-error">{error}</div>
              )}

              {selectedDate && selectedSlot && (
                <div className="bg-slate-50 border border-slate-100 p-3 text-sm font-['IBM_Plex_Sans'] text-slate-600">
                  Selected: {selectedDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} at {selectedSlot} EST
                </div>
              )}

              <Button type="submit" disabled={!selectedDate || !selectedSlot || !form.name || !form.email || !form.website || loading}
                className="w-full bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-11 font-['Outfit'] font-medium disabled:opacity-40" data-testid="booking-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Booking"}
              </Button>
              {!user && <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] text-center">No account needed to book a call.</p>}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
